import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LoadingScreen from '../../../../components/wizard/LoadingScreen';
import CityAutocomplete from '../../../../components/base/CityAutocomplete';
import Step1Profile from './Step1Profile';
import Step2Style from './Step2Style';
import Step3Regions from './Step3Regions';
import Step4Rhythm from './Step4Rhythm';
import Step5Time from './Step5Time';
import Step6PreviousTrips from './Step6PreviousTrips';
import Step7MustDoExperiences from './Step7MustDoExperiences';
import Step8AvoidExperiences from './Step8AvoidExperiences';
import Step9SpecialNeeds from './Step9SpecialNeeds';
import { WizardData } from '../../../../types/wizard';
import { brazilianCities, destinations } from '../../../../mocks/cities';
import { supabase, supabaseAnonKey, supabaseUrl } from '../../../../lib/supabase';
import { getVerifiedSupabaseSession } from '../../../../lib/supabaseAuth';
import {
  buildCanonicalRecommendationProfile,
  getRecommendationQualificationSummary,
  getRecommendationRoleLabel,
  rankDestinationRecommendations,
  type DestinationRecommendation,
} from '../../../../lib/recommendationIntegrity';
import {
  getCountryLabel,
  getRegionLabel,
  getRhythmLabel,
  getTravelStyleLabel,
} from '../../../../lib/recommendationPresentation';
import {
  applyAuthenticatedCustomerPrefill,
  applyDestinationMode,
  applyOptionalStepSkip,
  buildRecommendationRequestProfile,
  canSubmitWizard,
  getNextWizardStep,
  getPreviousWizardStep,
  validateWizardStep,
} from '../../../../lib/wizardJourney';

const TOTAL_STEPS = 10;

const DEFAULT_DATA: WizardData = {
  ageRange: '',
  occupation: '',
  lifestyle: '',
  travelStyle: '',
  travelStyles: [],
  regions: [],
  rhythm: '',
  timePreference: '',
  wakesUpEarly: false,
  enjoysNightlife: false,
  previousTrips: [],
  mustDoExperiences: [],
  avoidExperiences: [],
  avoidanceStatus: 'not_answered',
  dietaryRestrictions: [],
  mobilityNeeds: [],
  healthConditions: [],
  specialNeedsNotes: '',
  specialNeedsStatus: 'not_answered',
  fullName: '',
  email: '',
  phone: '',
  origem: '',
  destino: '',
  dataInicio: '',
  dataFim: '',
  numeroAdultos: '1',
  numeroCriancas: '0',
  idadeCriancas: [],
  orcamento: '',
  flexibilidade: '',
  bagagemDespachada: '',
  transportPreference: '',
  flexible_dates: undefined,
  purchase_window: '',
  destinationMode: 'manual',
  destinationSuggestionChoice: '',
};

const TRAVEL_IDEA_PREFILLS: Record<string, Partial<WizardData>> = {
  'kenya-conservation': {
    destino: 'Kenya Safari & Conservation Journey',
    regions: ['África'],
    travelStyle: 'Aventura',
    travelStyles: ['Aventura', 'Cultural'],
    mustDoExperiences: ['Wildlife safari', 'Conservation-led lodge', 'Private/local guide'],
    destinationMode: 'manual',
  },
  'japan-golden-route': {
    destino: 'Japan Golden Route',
    regions: ['Ásia'],
    travelStyle: 'Cultural',
    travelStyles: ['Cultural', 'Gastronomia'],
    mustDoExperiences: ['Tokyo', 'Kyoto', 'Rail logistics'],
    destinationMode: 'manual',
  },
  'paris-champagne-loire': {
    destino: 'Paris, Champagne & Loire',
    regions: ['Europa'],
    travelStyle: 'Gastronomia',
    travelStyles: ['Gastronomia', 'Cultural', 'Luxo'],
    mustDoExperiences: ['Paris', 'Champagne', 'Loire Valley'],
    destinationMode: 'manual',
  },
  'lencois-maranhenses': {
    destino: 'Lençóis Maranhenses',
    regions: ['América do Sul'],
    travelStyle: 'Aventura',
    travelStyles: ['Aventura', 'Relaxamento'],
    mustDoExperiences: ['Lagoons', 'Crossings', 'Local logistics'],
    destinationMode: 'manual',
  },
  'maldives-island-escape': {
    destino: 'Maldives Island Escape',
    regions: ['Ásia'],
    travelStyle: 'Lua de Mel',
    travelStyles: ['Lua de Mel', 'Relaxamento', 'Luxo'],
    mustDoExperiences: ['Reef access', 'Resort rhythm', 'Smooth transfers'],
    destinationMode: 'manual',
  },
  'victoria-falls-chobe': {
    destino: 'Victoria Falls & Chobe Extension',
    regions: ['África'],
    travelStyle: 'Aventura',
    travelStyles: ['Aventura', 'Cultural'],
    mustDoExperiences: ['Victoria Falls', 'Chobe safari', 'Regional logistics'],
    destinationMode: 'manual',
  },
};

interface DestinationSuggestion {
  candidateId: string;
  name: string;
  country: string;
  icon: string;
  reason: string;
  fitAngle?: string;
  highlights?: string[];
  regions: string[];
  styles: string[];
  minBudgetTier: number;
  maxBudgetTier: number;
  familyFriendly?: boolean;
  score?: number;
  source?: 'catalog' | 'ai';
  integritySignals?: {
    role: DestinationRecommendation['role'];
    fit: DestinationRecommendation['fit'];
    budget: DestinationRecommendation['budgetAssessment'];
    season: DestinationRecommendation['seasonAssessment'];
    flight: DestinationRecommendation['flightAssessment'];
    conflicts: string[];
    unevaluated: string[];
    matchedSignals: string[];
    tradeoff: string;
    consultantNote: string;
  };
}

type SupportedWizardLanguage = 'pt' | 'en';

interface RecommendDestinationsAiResult {
  success?: boolean;
  suggestions?: Array<Pick<DestinationSuggestion, 'candidateId'> & Partial<DestinationSuggestion>>;
  error?: string;
}

function getWizardLanguage(language?: string): SupportedWizardLanguage {
  return language?.startsWith('en') ? 'en' : 'pt';
}

function getLocalizedTravelStyleLabel(style: string, language: SupportedWizardLanguage): string {
  return getTravelStyleLabel(style, language);
}

function getLocalizedRegionLabel(region: string, language: SupportedWizardLanguage): string {
  return getRegionLabel(region, language);
}

function getLocalizedCountryLabel(country: string, language: SupportedWizardLanguage): string {
  return getCountryLabel(country, language);
}

function normalizeText(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function getLocalizedDestinationReason(
  destinationName: string,
  fallback: string,
  language: SupportedWizardLanguage,
  source: DestinationSuggestion['source'] = 'catalog',
): string {
  if (source === 'ai') return fallback;
  if (language === 'pt') return fallback;

  const reasons: Record<string, string> = {
    Paris:
      'Ideal for travelers looking for sophistication, culture and the classic feeling of a major capital.',
    Dubai:
      'Combines high-end comfort, visual impact and smoother logistics for a premium trip.',
    Orlando:
      'Perfect for light, memorable family travel, with child-friendly structure and ease.',
    Roma:
      'A strong choice for travelers who value history, atmosphere and deeply cultural experiences.',
    'Tóquio':
      'Excellent for travelers who enjoy intensity, novelty, organization and experiences beyond the obvious.',
    'Nova York':
      'Makes sense for profiles seeking urban energy, maximum variety and the feeling of an iconic trip.',
    Barcelona:
      'Delivers art, gastronomy and a relaxed rhythm in a destination that feels balanced for different traveler profiles.',
    'Cancún':
      'A great fit for travelers who want beaches, resorts and an easy-to-enjoy trip without too much complexity.',
    Lisboa:
      'A strong mix of European charm, pleasant pace and good value within a premium trip.',
    Maldivas:
      'A natural fit when romance, rest and a sense of total exclusivity are the top priorities.',
  };

  return reasons[destinationName] ?? fallback;
}

function getFallbackDestinationFitAngle(
  destination: DestinationSuggestion,
  language: SupportedWizardLanguage,
): string {
  const styleKeys = destination.styles.map(normalizeText);
  const regionKeys = destination.regions.map(normalizeText);

  if (styleKeys.includes(normalizeText('Família')) || destination.familyFriendly) {
    return language === 'en' ? 'Best for easy family flow' : 'Melhor para fluxo leve em família';
  }

  if (styleKeys.includes(normalizeText('Gastronomia'))) {
    return language === 'en' ? 'Best for food-led travel' : 'Melhor para viagem guiada por gastronomia';
  }

  if (styleKeys.includes(normalizeText('Lua de Mel'))) {
    return language === 'en' ? 'Best for romantic atmosphere' : 'Melhor para atmosfera romântica';
  }

  if (styleKeys.includes(normalizeText('Cultural')) || regionKeys.includes(normalizeText('Europa'))) {
    return language === 'en' ? 'Best for cultural depth' : 'Melhor para densidade cultural';
  }

  if (styleKeys.includes(normalizeText('Luxo'))) {
    return language === 'en' ? 'Best for comfort and polish' : 'Melhor para conforto e refinamento';
  }

  if (styleKeys.includes(normalizeText('Aventura'))) {
    return language === 'en' ? 'Best for active discovery' : 'Melhor para descoberta ativa';
  }

  return language === 'en' ? 'Best for overall fit' : 'Melhor encaixe geral';
}

function getSelectedTravelStyles(data: WizardData): string[] {
  const styles = (data.travelStyles ?? []).filter(Boolean);
  if (styles.length > 0) return styles;
  return data.travelStyle ? [data.travelStyle] : [];
}

function getPrimaryTravelStyle(data: WizardData): string {
  return getSelectedTravelStyles(data)[0] ?? data.travelStyle ?? '';
}

function formatTravelStyles(data: WizardData, language: SupportedWizardLanguage = 'pt'): string {
  const styles = getSelectedTravelStyles(data);
  return styles.length > 0
    ? styles.map((style) => getLocalizedTravelStyleLabel(style, language)).join(' • ')
    : '—';
}

function buildDestinationAiTraceId(): string {
  try {
    return `dest-ai-${crypto.randomUUID()}`;
  } catch {
    return `dest-ai-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }
}

async function invokeRecommendDestinationsAi(payload: Record<string, unknown>) {
  if (!supabaseUrl || !supabaseAnonKey) {
    return {
      data: null as RecommendDestinationsAiResult | null,
      error: new Error('Supabase destination AI endpoint is not configured'),
      traceId: '',
    };
  }

  const traceId = buildDestinationAiTraceId();
  const response = await fetch(`${supabaseUrl}/functions/v1/recommend-destinations-ai`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      apikey: supabaseAnonKey,
      Authorization: `Bearer ${supabaseAnonKey}`,
      'x-trace-id': traceId,
    },
    body: JSON.stringify(payload),
  });

  const raw = await response.text();
  let parsed: RecommendDestinationsAiResult | null = null;

  try {
    parsed = raw ? (JSON.parse(raw) as RecommendDestinationsAiResult) : null;
  } catch {
    parsed = null;
  }

  if (!response.ok) {
    return {
      data: parsed,
      error: new Error(parsed?.error ?? `Destination AI request failed with status ${response.status}`),
      traceId,
    };
  }

  return {
    data: parsed,
    error: null,
    traceId,
  };
}

function getBudgetTier(budget: string): number {
  const tiers: Record<string, number> = {
    'Até R$ 5.000': 1,
    'R$ 5.000 a R$ 10.000': 2,
    'R$ 10.000 a R$ 20.000': 3,
    'R$ 20.000 a R$ 35.000': 4,
    'R$ 35.000 a R$ 50.000': 5,
    'R$ 50.000 a R$ 80.000': 6,
    'Acima de R$ 80.000': 7,
  };

  return tiers[budget] ?? 3;
}

function getDestinationSuggestions(
  data: WizardData,
  language: SupportedWizardLanguage = 'pt',
): DestinationSuggestion[] {
  const profile = buildCanonicalRecommendationProfile(data, language);
  const rankedRecommendations = rankDestinationRecommendations(profile);

  return rankedRecommendations.map((item) => ({
    candidateId: item.candidateId,
    name: item.name,
    country: item.country,
    icon: item.icon,
    reason: item.reason,
    fitAngle: item.highlights[0] ?? getFallbackDestinationFitAngle({
      candidateId: item.candidateId,
      name: item.name,
      country: item.country,
      icon: item.icon,
      reason: item.reason,
      regions: item.regions,
      styles: item.styles,
      minBudgetTier: item.minBudgetTier,
      maxBudgetTier: item.maxBudgetTier,
    }, language),
    highlights: [
      ...item.matchedSignals,
      item.budgetAssessment.label,
      item.seasonAssessment.label,
    ].filter(Boolean).slice(0, 4),
    regions: item.regions,
    styles: item.styles,
    minBudgetTier: item.minBudgetTier,
    maxBudgetTier: item.maxBudgetTier,
    familyFriendly: item.familyFriendly,
    score: item.internalScore,
    source: 'catalog' as const,
    integritySignals: {
      role: item.role,
      fit: item.fit,
      budget: item.budgetAssessment,
      season: item.seasonAssessment,
      flight: item.flightAssessment,
      conflicts: item.conflicts,
      unevaluated: item.unevaluated,
      matchedSignals: item.matchedSignals,
      tradeoff: item.tradeoff,
      consultantNote: item.consultantNote,
    },
  }));
}

function buildRecommendationHighlights(
  data: WizardData,
  destination: DestinationSuggestion,
  language: SupportedWizardLanguage = 'pt',
): string[] {
  if (destination.highlights && destination.highlights.length > 0) {
    return destination.highlights;
  }

  const highlights: string[] = [];
  const selectedRegions = data.regions ?? [];
  const budgetTier = getBudgetTier(data.orcamento ?? '');
  const childrenCount = parseInt(data.numeroCriancas || '0', 10) || 0;
  const selectedStyles = getSelectedTravelStyles(data);

  const matchingStyles = selectedStyles.filter((style) => destination.styles.includes(style));
  if (matchingStyles.length > 0) {
    const localizedStyles = matchingStyles.map((style) => getLocalizedTravelStyleLabel(style, language)).join(', ');
    highlights.push(
      language === 'en'
        ? `It directly matches the travel styles you selected: ${localizedStyles}.`
        : `Combina diretamente com os estilos que você escolheu: ${localizedStyles}.`,
    );
  }

  const matchingRegions = selectedRegions.filter((region) => destination.regions.includes(region));
  if (matchingRegions.length > 0) {
    const localizedRegions = matchingRegions.map((region) => getLocalizedRegionLabel(region, language)).join(', ');
    highlights.push(
      language === 'en'
        ? `It sits inside the regions that caught your attention the most: ${localizedRegions}.`
        : `Está dentro das regiões que mais chamaram sua atenção: ${localizedRegions}.`,
    );
  }

  if (budgetTier >= destination.minBudgetTier && budgetTier <= destination.maxBudgetTier) {
    highlights.push(
      language === 'en'
        ? 'It makes sense within the budget range you indicated for this trip.'
        : 'Faz sentido dentro da faixa de orçamento que você indicou para essa viagem.',
    );
  }

  if ((selectedStyles.includes('Família') || childrenCount > 0) && destination.familyFriendly) {
    highlights.push(
      language === 'en'
        ? 'It is a strong fit for family travel, with a friendlier structure for that context.'
        : 'Tem boa aderência para uma viagem em família, com estrutura mais amigável para esse contexto.',
    );
  }

  if (highlights.length === 0) {
    highlights.push(
      language === 'en'
        ? 'It was prioritized because it creates a balanced mix of desire, feasibility and experience potential.'
        : 'Foi priorizado porque cria uma combinação equilibrada entre desejo, viabilidade e potencial de experiência.',
    );
  }

  return highlights;
}

function buildAIPrompt(data: WizardData): string {
  const selectedStyles = getSelectedTravelStyles(data);
  const start = new Date(data.dataInicio || '');
  const end = new Date(data.dataFim || '');
  const days = data.dataFim ? Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) : 7;
  return `
ROTEIRO PERSONALIZADO — AERO DESTINY

PERFIL DO VIAJANTE:
- Faixa etária: ${data.ageRange}
- Ocupação: ${data.occupation}
- Estilo de vida: ${data.lifestyle}
- Estilo principal de viagem: ${getPrimaryTravelStyle(data) || 'Não informado'}
- Estilos complementares: ${selectedStyles.slice(1).join(', ') || 'Nenhum'}
- Ritmo: ${data.rhythm}
- Preferência de horário: ${data.timePreference}

DADOS DA VIAGEM:
- Origem: ${data.origem}
- Destino: ${data.destino}
- Período: ${data.dataInicio} a ${data.dataFim} (${days} dias)
- Adultos: ${data.numeroAdultos} | Crianças: ${data.numeroCriancas}
- Orçamento: ${data.orcamento}

REGIÕES DE INTERESSE: ${(data.regions || []).join(', ')}
EXPERIÊNCIAS OBRIGATÓRIAS: ${(data.mustDoExperiences || []).join(', ')}
EXPERIÊNCIAS A EVITAR: ${(data.avoidExperiences || []).join(', ')}
RESTRIÇÕES ALIMENTARES: ${(data.dietaryRestrictions || []).join(', ')}
NECESSIDADES DE MOBILIDADE: ${(data.mobilityNeeds || []).join(', ')}
CONDIÇÕES DE SAÚDE: ${(data.healthConditions || []).join(', ')}
DESTINOS ANTERIORES: ${(data.previousTrips || []).join(', ')}
OBSERVAÇÕES: ${data.specialNeedsNotes || 'Nenhuma'}

Gere um roteiro premium, dia a dia, personalizado para este perfil.
`.trim();
}

function calculatePrice(data: WizardData): number {
  const primaryStyle = getPrimaryTravelStyle(data);
  const start = new Date(data.dataInicio || '');
  const end = new Date(data.dataFim || '');
  const days = data.dataFim ? Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) : 7;
  const adults = parseInt(data.numeroAdultos || '1', 10);
  const children = parseInt(data.numeroCriancas || '0', 10);
  const total = adults + children;
  const costMap: Record<string, number> = {
    Luxo: 1200, Executivo: 900, 'Lua de Mel': 800,
    Gastronomia: 700, Cruzeiro: 600, Aventura: 500,
    Cultural: 400, Família: 350,
  };
  const daily = costMap[primaryStyle] || 300;
  return Math.round(daily * days * total * 1.2);
}

export default function WizardFlow() {
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<WizardData>(DEFAULT_DATA);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const wizardRef = useRef<HTMLDivElement>(null);
  const ideaPrefillAppliedRef = useRef(false);
  const authPrefillAppliedRef = useRef(false);
  const wizardLanguage = getWizardLanguage(i18n.resolvedLanguage);

  useEffect(() => {
    if (ideaPrefillAppliedRef.current) return;

    const ideaKey = new URLSearchParams(window.location.search).get('idea') || '';
    const prefill = TRAVEL_IDEA_PREFILLS[ideaKey];

    if (!prefill) return;

    ideaPrefillAppliedRef.current = true;

    setFormData((prev) => ({
      ...prev,
      ...prefill,
      destinationSuggestionChoice: prefill.destino || prev.destinationSuggestionChoice,
      destinationSuggestionReason:
        wizardLanguage === 'en'
          ? 'Started from a curated Aero Destiny travel idea.'
          : 'Começou a partir de uma ideia de viagem curada pela Aero Destiny.',
    }));
  }, [wizardLanguage]);

  useEffect(() => {
    if (!supabase || authPrefillAppliedRef.current) return;
    let active = true;

    const prefillAuthenticatedCustomer = async () => {
      const session = await getVerifiedSupabaseSession();
      if (!active || !session?.user) return;
      authPrefillAppliedRef.current = true;
      const metadata = session.user.user_metadata ?? {};
      const profileName = [metadata.full_name, metadata.name, metadata.display_name]
        .find((value): value is string => typeof value === 'string' && Boolean(value.trim()));
      setFormData((previous) => applyAuthenticatedCustomerPrefill(previous, {
        email: session.user.email,
        name: profileName,
      }));
    };

    void prefillAuthenticatedCustomer();
    return () => {
      active = false;
    };
  }, []);

  const scrollToWizardTop = () => {
    if (wizardRef.current) {
      const offset = wizardRef.current.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: offset, behavior: 'smooth' });
    }
  };

  const updateField = (field: keyof WizardData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field as string];
      return next;
    });
  };

  const updateFormData = (data: Partial<WizardData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(data).forEach((k) => delete next[k]);
      return next;
    });
  };

  const handleNext = () => {
    const stepErrors = validateWizardStep(currentStep, formData, wizardLanguage);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      scrollToWizardTop();
      return;
    }
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep((step) => getNextWizardStep(step));
      scrollToWizardTop();
    } else {
      void handleGenerate();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((step) => getPreviousWizardStep(step));
      setErrors({});
      scrollToWizardTop();
    }
  };

  const handleOptionalStepSkip = (step: number) => {
    setFormData((previous) => applyOptionalStepSkip(previous, step));
    setErrors({});
    setCurrentStep(getNextWizardStep(step));
    scrollToWizardTop();
  };

  const handleGenerate = async () => {
    const submissionErrors = validateWizardStep(TOTAL_STEPS, formData, wizardLanguage);
    if (Object.keys(submissionErrors).length > 0) {
      setErrors(submissionErrors);
      setCurrentStep(TOTAL_STEPS);
      scrollToWizardTop();
      return;
    }

    setIsGenerating(true);
    try {
      const aiItinerary = buildAIPrompt(formData);
      const estimatedPrice = calculatePrice(formData);
      const primaryTravelStyle = getPrimaryTravelStyle(formData) || 'Luxo';
      const selectedTravelStyles = getSelectedTravelStyles(formData);

      // ⚠️ PAYLOAD IDDAS — enviado apenas na página de resultado (evita duplicata)
      void aiItinerary;
      void estimatedPrice;

      const resultData = {
        nomeCompleto: formData.fullName || '',
        email: formData.email || '',
        whatsapp: formData.phone || '',
        origem: formData.origem || '',
        destino: formData.destino || '',
        dataInicio: formData.dataInicio || '',
        dataFim: formData.dataFim || '',
        flexibilidade: formData.flexibilidade || 'nao',
        orcamento: formData.orcamento || '',
        numeroPessoas: formData.numeroAdultos || '1',
        numeroAdultos: formData.numeroAdultos || '1',
        numeroCriancas: formData.numeroCriancas || '0',
        idadeCriancas: formData.idadeCriancas || [],
        bagagemDespachada: formData.bagagemDespachada || 'nao',
        transportPreference: formData.transportPreference || '',
        tipoExperiencia: primaryTravelStyle,
        travelStyles: selectedTravelStyles,
        hotelDesejado: '',
        experienciasExclusivas: (formData.mustDoExperiences || []).join(', '),
        prazo: '',
        languagePreference: wizardLanguage,
        destinationMode: formData.destinationMode || 'manual',
        destinationSuggestionChoice: formData.destinationSuggestionChoice || '',
        destinationSuggestionCountry: formData.destinationSuggestionCountry || '',
        destinationSuggestionFitAngle: formData.destinationSuggestionFitAngle || '',
        destinationSuggestionReason: formData.destinationSuggestionReason || '',
        destinationSuggestionHighlights: formData.destinationSuggestionHighlights || [],
      };

      try {
        sessionStorage.setItem('itineraryFormData', JSON.stringify(resultData));
        sessionStorage.setItem('itineraryFormData_backup', JSON.stringify(resultData));
        sessionStorage.setItem('itineraryFormData_draft', JSON.stringify(resultData));
      } catch { /* Safari private mode */ }

      navigate('/resultado', { state: { formData: resultData } });
    } catch (err) {
      console.error('Erro ao gerar roteiro:', err instanceof Error ? err.message : String(err));
      setIsGenerating(false);
    }
  };

  if (isGenerating) {
    return <LoadingScreen isVisible />;
  }

  const stepInfo = {
    title: t(`wizard.steps.${currentStep}.title`),
    description: t(`wizard.steps.${currentStep}.description`),
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1Profile
            formData={{
              step1_profile: {
                ageRange: formData.ageRange as any,
                occupation: formData.occupation as any,
                lifestyle: formData.lifestyle as any,
              },
            } as any}
            updateFormData={(d: any) => {
              if (d.step1_profile) {
                updateFormData({
                  ageRange: d.step1_profile.ageRange ?? formData.ageRange,
                  occupation: d.step1_profile.occupation ?? formData.occupation,
                  lifestyle: d.step1_profile.lifestyle ?? formData.lifestyle,
                });
              }
            }}
            errors={errors}
          />
        );
      case 2:
        return (
          <Step2Style
            formData={{
              step2_style: formData.travelStyle as any,
              step2_styles: (formData.travelStyles ?? []) as any,
            }}
            updateFormData={(d: any) => {
              if (d.step2_styles !== undefined || d.step2_style !== undefined) {
                updateFormData({
                  travelStyles: d.step2_styles ?? formData.travelStyles ?? [],
                  travelStyle: d.step2_style ?? d.step2_styles?.[0] ?? '',
                });
              }
            }}
            errors={errors}
          />
        );
      case 3:
        return (
          <Step3Regions
            formData={{ step3_regions: formData.regions || [] } as any}
            updateFormData={(d: any) => {
              if (d.step3_regions !== undefined) updateField('regions', d.step3_regions);
            }}
            errors={errors}
          />
        );
      case 4:
        return (
          <Step4Rhythm
            formData={{ step4_rhythm: formData.rhythm } as any}
            updateFormData={(d: any) => {
              if (d.step4_rhythm !== undefined) updateField('rhythm', d.step4_rhythm);
            }}
            errors={errors}
          />
        );
      case 5:
        return (
          <Step5Time
            formData={{
              step5_time: {
                preferredTime: formData.timePreference,
                earlyRiser: formData.wakesUpEarly,
                nightOwl: formData.enjoysNightlife,
              },
            } as any}
            updateFormData={(d: any) => {
              if (d.step5_time) {
                updateFormData({
                  timePreference: d.step5_time.preferredTime ?? formData.timePreference,
                  wakesUpEarly: d.step5_time.earlyRiser ?? formData.wakesUpEarly,
                  enjoysNightlife: d.step5_time.nightOwl ?? formData.enjoysNightlife,
                });
              }
            }}
            errors={errors}
          />
        );
      case 6:
        return (
          <Step6PreviousTrips
            data={formData}
            onChange={updateField}
            onAdvance={() => handleOptionalStepSkip(6)}
          />
        );
      case 7:
        return (
          <Step7MustDoExperiences
            data={formData}
            onChange={updateField}
            error={errors.mustDoExperiences}
          />
        );
      case 8:
        return (
          <Step8AvoidExperiences
            data={formData}
            onChange={updateField}
            onAdvance={() => handleOptionalStepSkip(8)}
          />
        );
      case 9:
        return (
          <Step9SpecialNeeds
            data={formData}
            onChange={updateField}
            onAdvance={() => handleOptionalStepSkip(9)}
          />
        );
      case 10:
        return (
          <Step10Form
            formData={formData}
            updateFormData={updateFormData}
            errors={errors}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div ref={wizardRef} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-5 sm:p-8 shadow-2xl">
      {/* Barra de progresso */}
      <div className="mb-8">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <span className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest">
            {wizardLanguage === 'en'
              ? `Step ${t('wizard.nav.stepCounter', { current: currentStep, total: TOTAL_STEPS })}`
              : `Etapa ${t('wizard.nav.stepCounter', { current: currentStep, total: TOTAL_STEPS })}`}
          </span>
          <span className="text-white/90 text-xs">
            {Math.round((currentStep / TOTAL_STEPS) * 100)}% {t('wizard.nav.progressComplete')}
          </span>
        </div>
        <div className="relative h-1.5 bg-white/10 rounded-full overflow-hidden mb-5">
          <div
            className="absolute left-0 top-0 h-full bg-gradient-to-r from-[#C9A84C] to-[#E8D4A0] rounded-full transition-all duration-700 ease-out"
            style={{ width: `${(currentStep / TOTAL_STEPS) * 100}%` }}
          />
        </div>
        <h3 className="text-lg sm:text-xl font-bold text-white">{stepInfo.title}</h3>
        <p className="text-white/90 text-sm mt-1">{stepInfo.description}</p>
      </div>

      {/* Conteúdo do step */}
      <div className="mb-8">{renderStep()}</div>

      {/* Navegação */}
      <div className="flex items-center justify-between gap-4 pt-6 border-t border-white/10">
        <button
          type="button"
          onClick={handleBack}
          className={`px-6 py-3 rounded-xl font-semibold transition-all whitespace-nowrap flex items-center gap-2 ${
            currentStep === 1
              ? 'opacity-0 pointer-events-none'
              : 'bg-white/5 border border-white/20 text-white hover:bg-white/10 cursor-pointer'
          }`}
        >
          <i className="ri-arrow-left-line"></i> {t('wizard.nav.back')}
        </button>

        <button
          type="button"
          onClick={handleNext}
          disabled={currentStep === TOTAL_STEPS && !canSubmitWizard(formData, wizardLanguage)}
          aria-disabled={currentStep === TOTAL_STEPS && !canSubmitWizard(formData, wizardLanguage)}
          className="flex-1 sm:flex-none sm:min-w-[220px] sm:max-w-xs py-3.5 bg-gradient-to-r from-[#9E741D] to-[#C9A84C] text-white font-extrabold rounded-xl hover:shadow-[0_0_30px_rgba(201,168,76,0.4)] transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none"
        >
          {currentStep === TOTAL_STEPS ? (
            <>
              <i className="ri-sparkling-2-line text-lg"></i>
              {t('wizard.nav.generate')}
            </>
          ) : (
            <>
              {t('wizard.nav.next')}
              <i className="ri-arrow-right-line text-lg"></i>
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ─── Step 10: Dados da viagem + contato ──────────────────────────────────────
interface Step10FormProps {
  formData: WizardData;
  updateFormData: (d: Partial<WizardData>) => void;
  errors: Record<string, string>;
}

function Step10Form({ formData, updateFormData, errors }: Step10FormProps) {
  const { i18n, t } = useTranslation();
  const wizardLanguage = getWizardLanguage(i18n.resolvedLanguage);
  const adultOptions = Array.from({ length: 9 }, (_, index) => String(index + 1));
  const childrenOptions = Array.from({ length: 10 }, (_, index) => String(index));
  const destinationMode = formData.destinationMode ?? 'manual';
  const selectedTravelStyles = useMemo(
    () => getSelectedTravelStyles(formData),
    [formData],
  );
  const [aiSuggestedDestinations, setAiSuggestedDestinations] = useState<DestinationSuggestion[] | null>(null);
  const [isRefiningSuggestions, setIsRefiningSuggestions] = useState(false);
  const [aiSuggestionStatus, setAiSuggestionStatus] = useState<'idle' | 'ai' | 'fallback'>('idle');
  const suggestionRequestRef = useRef(0);
  const suggestionContextKey = JSON.stringify({
    destinationMode,
    language: wizardLanguage,
    regions: formData.regions ?? [],
    styles: selectedTravelStyles,
    budget: formData.orcamento ?? '',
    origin: formData.origem ?? '',
    startDate: formData.dataInicio ?? '',
    endDate: formData.dataFim ?? '',
    previousTrips: formData.previousTrips ?? [],
    adults: formData.numeroAdultos ?? '1',
    children: formData.numeroCriancas ?? '0',
    ageRange: formData.ageRange ?? '',
    lifestyle: formData.lifestyle ?? '',
    rhythm: formData.rhythm ?? '',
    timePreference: formData.timePreference ?? '',
    mustDoExperiences: formData.mustDoExperiences ?? [],
    avoidExperiences: formData.avoidExperiences ?? [],
  });
  const catalogSuggestedDestinations = useMemo(
    () =>
      getDestinationSuggestions(formData, wizardLanguage).map((item) => ({
        ...item,
        fitAngle: item.fitAngle || getFallbackDestinationFitAngle(item, wizardLanguage),
        source: 'catalog' as const,
      })),
    [formData, wizardLanguage],
  );
  const qualificationSummary = useMemo(
    () => getRecommendationQualificationSummary(
      buildCanonicalRecommendationProfile(formData, wizardLanguage),
    ),
    [formData, wizardLanguage],
  );

  useEffect(() => {
    if (destinationMode !== 'suggested') {
      setAiSuggestedDestinations(null);
      setIsRefiningSuggestions(false);
      setAiSuggestionStatus('idle');
      return;
    }

    if (catalogSuggestedDestinations.length === 0) {
      suggestionRequestRef.current += 1;
      setAiSuggestedDestinations([]);
      setIsRefiningSuggestions(false);
      setAiSuggestionStatus('idle');
      return;
    }

    if (!supabaseUrl || !supabaseAnonKey) {
      setAiSuggestedDestinations(catalogSuggestedDestinations);
      setIsRefiningSuggestions(false);
      setAiSuggestionStatus('fallback');
      return;
    }

    const requestId = ++suggestionRequestRef.current;
    setAiSuggestedDestinations(catalogSuggestedDestinations);
    setIsRefiningSuggestions(true);
    setAiSuggestionStatus('idle');

    const timer = window.setTimeout(async () => {
      const softTimeout = window.setTimeout(() => {
        if (requestId !== suggestionRequestRef.current) return;
        setIsRefiningSuggestions(false);
        setAiSuggestionStatus('idle');
      }, 4500);

      try {
        const { data, error, traceId } = await invokeRecommendDestinationsAi({
          language: wizardLanguage,
          profile: buildRecommendationRequestProfile({
            origem: formData.origem,
            dataInicio: formData.dataInicio,
            dataFim: formData.dataFim,
            ageRange: formData.ageRange,
            occupation: formData.occupation,
            lifestyle: formData.lifestyle,
            travelStyle: formData.travelStyle,
            travelStyles: selectedTravelStyles,
            rhythm: formData.rhythm,
            timePreference: formData.timePreference,
            regions: formData.regions,
            orcamento: formData.orcamento,
            previousTrips: formData.previousTrips,
            mustDoExperiences: formData.mustDoExperiences,
            avoidExperiences: formData.avoidExperiences,
            numeroAdultos: formData.numeroAdultos,
            numeroCriancas: formData.numeroCriancas,
          }),
          candidates: catalogSuggestedDestinations,
        });

        if (requestId !== suggestionRequestRef.current) return;
        window.clearTimeout(softTimeout);

        if (error || !data?.success || !Array.isArray(data?.suggestions) || data.suggestions.length === 0) {
          console.warn('Destination AI fallback active', {
            traceId,
            error: error?.message ?? data?.error ?? 'No valid suggestions returned',
          });
          setAiSuggestedDestinations(catalogSuggestedDestinations);
          setAiSuggestionStatus('fallback');
          setIsRefiningSuggestions(false);
          return;
        }

        const seenCandidateIds = new Set<string>();
        const aiOrderedSuggestions = data.suggestions.flatMap((suggestion) => {
          const candidateId = typeof suggestion.candidateId === 'string' ? suggestion.candidateId : '';
          const base = catalogSuggestedDestinations.find((candidate) => candidate.candidateId === candidateId);
          if (!base || seenCandidateIds.has(candidateId)) return [];
          seenCandidateIds.add(candidateId);

          return [{
            ...base,
            reason: base.reason,
            highlights: base.highlights,
            fitAngle: base.fitAngle || getFallbackDestinationFitAngle(base, wizardLanguage),
            score: base.score,
            source: 'ai' as const,
          }];
        });
        const mergedSuggestions = [
          ...aiOrderedSuggestions,
          ...catalogSuggestedDestinations.filter((candidate) => !seenCandidateIds.has(candidate.candidateId)),
        ].slice(0, 3);

        setAiSuggestedDestinations(
          aiOrderedSuggestions.length > 0 ? mergedSuggestions : catalogSuggestedDestinations,
        );
        setAiSuggestionStatus(aiOrderedSuggestions.length > 0 ? 'ai' : 'fallback');
        setIsRefiningSuggestions(false);
      } catch (invokeError) {
        console.error('Erro ao refinar sugestões de destino com IA:', invokeError);
        if (requestId !== suggestionRequestRef.current) return;
        setAiSuggestedDestinations(catalogSuggestedDestinations);
        setAiSuggestionStatus('fallback');
        setIsRefiningSuggestions(false);
      }
    }, 250);

    return () => {
      window.clearTimeout(timer);
    };
  }, [catalogSuggestedDestinations, destinationMode, formData.ageRange, formData.avoidExperiences, formData.dataFim, formData.dataInicio, formData.lifestyle, formData.mustDoExperiences, formData.numeroAdultos, formData.numeroCriancas, formData.occupation, formData.orcamento, formData.origem, formData.previousTrips, formData.regions, formData.rhythm, formData.timePreference, formData.travelStyle, selectedTravelStyles, suggestionContextKey, wizardLanguage]);

  const suggestedDestinations = aiSuggestedDestinations ?? catalogSuggestedDestinations;
  const suggestionCountLabel =
    suggestedDestinations.length === 1
      ? t('wizard.step10.suggestionsTitleOne')
      : t('wizard.step10.suggestionsTitleOther', { count: suggestedDestinations.length });
  const selectedSuggestedDestination =
    suggestedDestinations.find((destination) => destination.name === formData.destinationSuggestionChoice) ??
    suggestedDestinations[0] ??
    null;
  const recommendationHighlights = selectedSuggestedDestination
    ? buildRecommendationHighlights(formData, selectedSuggestedDestination, wizardLanguage)
    : [];
  const recommendationReviewChecks = selectedSuggestedDestination?.integritySignals
    ? [
        selectedSuggestedDestination.integritySignals.budget,
        selectedSuggestedDestination.integritySignals.season,
        selectedSuggestedDestination.integritySignals.flight,
      ]
    : [];
  const recommendationConflicts = selectedSuggestedDestination?.integritySignals?.conflicts ?? [];
  const recommendationUnknowns = selectedSuggestedDestination?.integritySignals?.unevaluated ?? [];
  const recommendationMatchedSignals = selectedSuggestedDestination?.integritySignals?.matchedSignals ?? [];
  const recommendationTradeoff = selectedSuggestedDestination?.integritySignals?.tradeoff ?? '';
  const copy = wizardLanguage === 'en'
    ? {
        profileSummary: 'Profile summary',
        style: 'Style',
        rhythm: 'Rhythm',
        regions: 'Regions',
        experiences: 'Experiences',
        restrictions: 'Restrictions',
        needs: 'Needs',
        selected: 'selected',
        mustHave: 'must-have',
        avoid: 'to avoid',
        informed: 'Informed',
        none: 'None',
        tripData: 'Trip Details',
        destinationQuestion: 'How would you like to define the destination?',
        destinationQuestionDescription:
          'We can continue with the destination you already have in mind or suggest options that match your profile before generating the itinerary.',
        departureDate: 'Departure Date',
        returnDate: 'Return Date',
        adults: 'Adults',
        children: 'Children',
        budgetPerPerson: 'Budget Per Traveler',
        budgetUsdHint: 'Approximate USD reference shown for convenience.',
        checkedBag: 'Checked luggage?',
        checkedBagYes: 'Yes, I need to check luggage',
        checkedBagNo: 'No, carry-on only',
        transportQuestion: 'Do you plan to rent a car or prefer a transfer?',
        transportHelper: 'Optional. This helps the consultant review arrival, transfers and local mobility.',
        transportOptions: [
          { value: 'transfer_driver', label: 'I prefer transfer / driver' },
          { value: 'rent_car', label: 'I want to rent a car' },
          { value: 'compare_both', label: 'I want to compare both options' },
          { value: 'not_needed_now', label: 'I do not need this now' },
        ],
        flexibleDates: 'Do you have flexible dates?',
        purchaseWindow: 'When do you plan to buy?',
        contactTitle: 'Your Contact Details',
        fullName: 'Full Name',
        email: 'Email',
        whatsapp: 'WhatsApp',
        fullNamePlaceholder: 'Your full name',
        phonePlaceholder: '+55 62 99999-9999',
        phoneHelper: 'Include the country code for WhatsApp. Example: +55 62 99999-9999.',
        privacy:
          'We use your information to plan your trip and provide requested services. Necessary providers may process relevant data.',
        budgetPlaceholder: 'Select...',
        destinationPlaceholder: 'Ex: Paris, Maldives...',
        destinationAutocompleteHelper: 'Choose a suggestion or keep typing to use any city, region or country.',
        customDestinationLabel: (value: string) => `Use “${value}” as the destination`,
        departurePlaceholder: 'Ex: Goiânia, São Paulo...',
        choose: 'Select...',
        yes: 'Yes',
        no: 'No',
      }
    : {
        profileSummary: 'Resumo do seu perfil',
        style: 'Estilo',
        rhythm: 'Ritmo',
        regions: 'Regiões',
        experiences: 'Experiências',
        restrictions: 'Restrições',
        needs: 'Necessidades',
        selected: 'selecionadas',
        mustHave: 'obrigatórias',
        avoid: 'a evitar',
        informed: 'Informadas',
        none: 'Nenhuma',
        tripData: 'Dados da Viagem',
        destinationQuestion: 'Como você quer definir o destino?',
        destinationQuestionDescription:
          'Podemos seguir com o destino que você já tem em mente ou sugerir opções compatíveis com o seu perfil antes de gerar o roteiro.',
        departureDate: 'Data de Ida',
        returnDate: 'Data de Volta',
        adults: 'Adultos',
        children: 'Crianças',
        budgetPerPerson: 'Orçamento por Pessoa',
        budgetUsdHint: 'Referência aproximada em USD exibida por conveniência.',
        checkedBag: 'Bagagem Despachada?',
        checkedBagYes: 'Sim, preciso despachar',
        checkedBagNo: 'Não, só bagagem de mão',
        transportQuestion: 'Você pretende alugar carro ou prefere transfer?',
        transportHelper: 'Opcional. Isso ajuda a consultoria a revisar chegada, transfers e deslocamentos locais.',
        transportOptions: [
          { value: 'transfer_driver', label: 'Quero transfer / motorista' },
          { value: 'rent_car', label: 'Quero alugar carro' },
          { value: 'compare_both', label: 'Quero avaliar as duas opções' },
          { value: 'not_needed_now', label: 'Não preciso agora' },
        ],
        flexibleDates: 'Você tem datas flexíveis?',
        purchaseWindow: 'Quando pretende comprar?',
        contactTitle: 'Seus Dados de Contato',
        fullName: 'Nome Completo',
        email: 'E-mail',
        whatsapp: 'WhatsApp',
        fullNamePlaceholder: 'Seu nome completo',
        phonePlaceholder: '+55 62 99999-9999',
        phoneHelper: 'Use o código do país no WhatsApp. Exemplo: +55 62 99999-9999.',
        privacy:
          'Usamos seus dados para planejar sua viagem e prestar os serviços solicitados. Provedores necessários podem tratar dados relacionados.',
        budgetPlaceholder: 'Selecione...',
        destinationPlaceholder: 'Ex: Paris, Maldivas...',
        destinationAutocompleteHelper: 'Escolha uma sugestão ou continue digitando para usar qualquer cidade, região ou país.',
        customDestinationLabel: (value: string) => `Usar “${value}” como destino`,
        departurePlaceholder: 'Ex: Goiânia, São Paulo...',
        choose: 'Selecione...',
        yes: 'Sim',
        no: 'Não',
      };
  const budgetOptions = [
    'Até R$ 5.000',
    'R$ 5.000 a R$ 10.000',
    'R$ 10.000 a R$ 20.000',
    'R$ 20.000 a R$ 35.000',
    'R$ 35.000 a R$ 50.000',
    'R$ 50.000 a R$ 80.000',
    'Acima de R$ 80.000',
  ];
  const budgetOptionDisplay = useMemo(
    () =>
      new Map<string, string>([
        [
          'Até R$ 5.000',
          wizardLanguage === 'en'
            ? 'Up to R$ 5,000  •  est. US$ 1k'
            : 'Até R$ 5.000  •  est. US$ 1 mil',
        ],
        [
          'R$ 5.000 a R$ 10.000',
          wizardLanguage === 'en'
            ? 'R$ 5,000 to R$ 10,000  •  est. US$ 1k–2k'
            : 'R$ 5.000 a R$ 10.000  •  est. US$ 1–2 mil',
        ],
        [
          'R$ 10.000 a R$ 20.000',
          wizardLanguage === 'en'
            ? 'R$ 10,000 to R$ 20,000  •  est. US$ 2k–4k'
            : 'R$ 10.000 a R$ 20.000  •  est. US$ 2–4 mil',
        ],
        [
          'R$ 20.000 a R$ 35.000',
          wizardLanguage === 'en'
            ? 'R$ 20,000 to R$ 35,000  •  est. US$ 4k–7k'
            : 'R$ 20.000 a R$ 35.000  •  est. US$ 4–7 mil',
        ],
        [
          'R$ 35.000 a R$ 50.000',
          wizardLanguage === 'en'
            ? 'R$ 35,000 to R$ 50,000  •  est. US$ 7k–10k'
            : 'R$ 35.000 a R$ 50.000  •  est. US$ 7–10 mil',
        ],
        [
          'R$ 50.000 a R$ 80.000',
          wizardLanguage === 'en'
            ? 'R$ 50,000 to R$ 80,000  •  est. US$ 10k–16k'
            : 'R$ 50.000 a R$ 80.000  •  est. US$ 10–16 mil',
        ],
        [
          'Acima de R$ 80.000',
          wizardLanguage === 'en'
            ? 'Above R$ 80,000  •  est. US$ 16k+'
            : 'Acima de R$ 80.000  •  est. US$ 16 mil+',
        ],
      ]),
    [wizardLanguage],
  );
  const purchaseWindowOptions = [
    { value: '0 a 15 dias', label: wizardLanguage === 'en' ? '0 to 15 days' : '0 a 15 dias' },
    { value: '15 a 45 dias', label: wizardLanguage === 'en' ? '15 to 45 days' : '15 a 45 dias' },
    { value: '45+ dias', label: wizardLanguage === 'en' ? '45+ days' : '45+ dias' },
  ];

  return (
    <div className="space-y-6">
      {/* Resumo do perfil */}
      <div className="p-5 bg-gradient-to-br from-[#C9A84C]/10 to-[#C9A84C]/5 border border-[#C9A84C]/20 rounded-2xl">
        <div className="flex items-center gap-2 mb-3">
          <i className="ri-user-star-line text-[#C9A84C]"></i>
          <span className="text-sm font-bold text-[#C9A84C]">{copy.profileSummary}</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[
            { label: copy.style, value: formatTravelStyles(formData, wizardLanguage) },
            { label: copy.rhythm, value: formData.rhythm ? getRhythmLabel(formData.rhythm, wizardLanguage) : '—' },
            { label: copy.regions, value: `${(formData.regions || []).length} ${copy.selected}` },
            { label: copy.experiences, value: `${(formData.mustDoExperiences || []).length} ${copy.mustHave}` },
            { label: copy.restrictions, value: `${(formData.avoidExperiences || []).length} ${copy.avoid}` },
            {
              label: copy.needs,
              value:
                (formData.dietaryRestrictions || []).length + (formData.mobilityNeeds || []).length > 0
                  ? copy.informed
                  : copy.none,
            },
          ].map((item) => (
            <div key={item.label} className="bg-white/5 rounded-xl p-3 border border-white/10">
              <span className="text-white/90 text-xs block mb-1">{item.label}</span>
              <span className="text-white font-semibold text-xs">{item.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Dados da viagem */}
      <div className="space-y-4">
        <h4 className="text-white font-bold text-base flex items-center gap-2">
          <i className="ri-plane-line text-[#C9A84C]"></i> {copy.tripData}
        </h4>

        <div className="rounded-2xl border border-[#C9A84C]/20 bg-gradient-to-br from-[#C9A84C]/10 to-white/5 p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#C9A84C]/15 flex-shrink-0">
              <i className="ri-magic-line text-[#C9A84C] text-lg"></i>
            </div>
            <div className="flex-1">
              <p className="text-white font-bold text-sm mb-1">{copy.destinationQuestion}</p>
              <p className="text-white/90 text-sm leading-relaxed mb-4">
                {copy.destinationQuestionDescription}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() =>
                    updateFormData(applyDestinationMode(formData, 'manual'))
                  }
                  className={`rounded-2xl border px-4 py-4 text-left transition-all ${
                    destinationMode === 'manual'
                      ? 'border-[#C9A84C]/30 bg-[#C9A84C]/10'
                      : 'border-white/10 bg-white/5 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-map-pin-line text-[#C9A84C]"></i>
                    <span className="text-white font-semibold text-sm">{t('wizard.step10.manualTitle')}</span>
                  </div>
                  <p className="text-white/90 text-xs leading-relaxed">
                    {t('wizard.step10.manualDescription')}
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() =>
                    updateFormData(applyDestinationMode(formData, 'suggested'))
                  }
                  className={`rounded-2xl border px-4 py-4 text-left transition-all ${
                    destinationMode === 'suggested'
                      ? 'border-[#C9A84C]/30 bg-[#C9A84C]/10'
                      : 'border-white/10 bg-white/5 hover:bg-white/10'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <i className="ri-sparkling-2-line text-[#C9A84C]"></i>
                    <span className="text-white font-semibold text-sm">{t('wizard.step10.suggestedTitle')}</span>
                  </div>
                  <p className="text-white/90 text-xs leading-relaxed">
                    {t('wizard.step10.suggestedDescription')}
                  </p>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-bold text-white/90 mb-2">
              {t('wizard.step10.departureCity')} <span className="text-[#C9A84C]">*</span>
            </label>
            <CityAutocomplete
              id="origem"
              name="origem"
              value={formData.origem || ''}
              onChange={(v) => updateFormData({ origem: v })}
              suggestions={brazilianCities}
              placeholder={copy.departurePlaceholder}
              icon="ri-map-pin-line"
              required
            />
            {errors.origem && <p className="text-red-400 text-xs mt-1">{errors.origem}</p>}
          </div>
          <div>
            {destinationMode === 'manual' ? (
              <>
                <label className="block text-sm font-bold text-white/90 mb-2">
                  {t('wizard.step10.destination')} <span className="text-[#C9A84C]">*</span>
                </label>
                <CityAutocomplete
                  id="destino"
                  name="destino"
                  value={formData.destino || ''}
                  onChange={(v) =>
                    updateFormData({
                      destino: v,
                      destinationSuggestionChoice: '',
                      destinationSuggestionCountry: '',
                      destinationSuggestionFitAngle: '',
                      destinationSuggestionReason: '',
                      destinationSuggestionHighlights: [],
                    })
                  }
                  suggestions={destinations}
                  placeholder={copy.destinationPlaceholder}
                  icon="ri-flight-takeoff-line"
                  allowCustomValue
                  customValueLabel={copy.customDestinationLabel}
                  required
                />
                <p className="mt-2 text-xs leading-relaxed text-white/60">
                  {copy.destinationAutocompleteHelper}
                </p>
                {errors.destino && <p className="text-red-400 text-xs mt-1">{errors.destino}</p>}
              </>
            ) : (
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4 h-full">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-compass-discover-line text-[#C9A84C]"></i>
                  <span className="text-white font-semibold text-sm">{t('wizard.step10.destinationSuggested')}</span>
                </div>
                <p className="text-white/90 text-xs leading-relaxed">
                  {t('wizard.step10.destinationSuggestedDescription')}
                </p>
              </div>
            )}
          </div>
        </div>

        {destinationMode === 'suggested' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="text-white font-semibold text-sm">{suggestionCountLabel}</p>
                <p className="text-white/90 text-xs">
                  {t('wizard.step10.suggestionsDescription')}
                </p>
                {qualificationSummary.explanation && (
                  <p className="mt-1 max-w-3xl text-xs leading-relaxed text-[#E8D4A0]">
                    {qualificationSummary.explanation}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {isRefiningSuggestions && (
                  <span className="inline-flex items-center gap-2 rounded-full border border-[#C9A84C]/20 bg-[#C9A84C]/10 px-3 py-1.5 text-[#E8D4A0] text-xs font-semibold">
                    <i className="ri-loader-4-line animate-spin"></i>
                    {wizardLanguage === 'en' ? 'Personalizing suggestions' : 'Personalizando sugestões'}
                  </span>
                )}
                {!isRefiningSuggestions && aiSuggestionStatus === 'ai' && (
                  <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-3 py-1.5 text-emerald-200 text-xs font-semibold">
                    <i className="ri-sparkling-2-line"></i>
                    {wizardLanguage === 'en' ? 'Matched to your profile' : 'Compatíveis com seu perfil'}
                  </span>
                )}
                {!isRefiningSuggestions && aiSuggestionStatus === 'fallback' && destinationMode === 'suggested' && (
                  <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-white/90 text-xs font-semibold">
                    <i className="ri-compass-3-line text-[#C9A84C]"></i>
                    {wizardLanguage === 'en' ? 'Curated by Aero Destiny' : 'Curadoria Aero Destiny'}
                  </span>
                )}
                {formData.destinationSuggestionChoice && (
                  <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-3 py-1.5 text-emerald-200 text-xs font-semibold">
                    <i className="ri-check-line"></i>
                    {formData.destinationSuggestionChoice} {t('wizard.step10.selectedSuffix')}
                  </span>
                )}
              </div>
            </div>

            {suggestedDestinations.length === 0 ? (
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="max-w-2xl">
                    <div className="flex items-center gap-2 mb-2">
                      <i className="ri-information-line text-[#C9A84C]"></i>
                      <span className="text-white font-semibold text-sm">
                        {t('wizard.step10.noSuggestionTitle')}
                      </span>
                    </div>
                    <p className="text-white/90 text-sm leading-relaxed">
                      {t('wizard.step10.noSuggestionDescription')}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      updateFormData(applyDestinationMode(formData, 'manual'))
                    }
                    className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-white text-sm font-semibold hover:bg-white/10 transition-all"
                  >
                    <i className="ri-arrow-left-line text-[#C9A84C]"></i>
                    {t('wizard.step10.backToManual')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
                {suggestedDestinations.map((destination) => {
                  const isSelected = formData.destinationSuggestionChoice === destination.name;

                  return (
                    <button
                      key={destination.name}
                      type="button"
                      onClick={() =>
                        updateFormData({
                          destino: destination.name,
                          destinationSuggestionChoice: destination.name,
                          destinationSuggestionCountry: destination.country,
                          destinationSuggestionFitAngle: destination.fitAngle ?? '',
                          destinationSuggestionReason: destination.reason,
                          destinationSuggestionHighlights: destination.highlights ?? [],
                        })
                      }
                      className={`rounded-2xl border p-4 text-left transition-all ${
                        isSelected
                          ? 'border-[#C9A84C]/30 bg-[#C9A84C]/10 shadow-[0_0_0_1px_rgba(201,168,76,0.08)]'
                          : 'border-white/10 bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#C9A84C]/10">
                            <i className={`${destination.icon} text-[#C9A84C] text-lg`}></i>
                          </div>
                          <div>
                            <p className="text-white font-semibold text-sm">{destination.name}</p>
                            <p className="text-white/90 text-xs">
                              {getLocalizedCountryLabel(destination.country, wizardLanguage)}
                            </p>
                          </div>
                        </div>
                        <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-white/90 font-semibold">
                          {getRecommendationRoleLabel(
                            destination.integritySignals?.role ?? 'best_overall',
                            wizardLanguage,
                          )}
                        </span>
                      </div>

                      {destination.fitAngle && (
                        <div className="mb-3">
                          <span className="inline-flex items-center rounded-full border border-[#C9A84C]/20 bg-[#C9A84C]/10 px-2.5 py-1 text-[11px] text-[#E8D4A0] font-semibold">
                            {destination.fitAngle}
                          </span>
                        </div>
                      )}

                      <p className="text-white/90 text-xs leading-relaxed mb-3">
                        {getLocalizedDestinationReason(destination.name, destination.reason, wizardLanguage, destination.source)}
                      </p>

                      <div className="flex items-center justify-between gap-3">
                        <span className="text-[#C9A84C] text-[11px] uppercase tracking-[0.18em] font-bold">
                          {t('wizard.step10.recommendedFor', {
                            styles: formatTravelStyles(formData, wizardLanguage),
                          })}
                        </span>
                        <span
                          className={`inline-flex items-center gap-1 text-xs font-semibold ${
                            isSelected ? 'text-[#E8D4A0]' : 'text-white/90'
                          }`}
                        >
                          {isSelected ? t('wizard.step10.selected') : t('wizard.step10.useDestination')}
                          <i className={isSelected ? 'ri-check-line' : 'ri-arrow-right-line'}></i>
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {errors.destino && <p className="text-red-400 text-xs mt-1">{errors.destino}</p>}
          </div>
        )}

        {destinationMode === 'suggested' && selectedSuggestedDestination && (
          <div className="rounded-2xl border border-[#C9A84C]/20 bg-gradient-to-br from-[#C9A84C]/10 via-white/5 to-white/5 p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
              <div>
                <p className="text-[#E8D4A0] text-[11px] font-bold uppercase tracking-[0.22em] mb-1">
                  {t('wizard.step10.whyRecommended')}
                </p>
                <h5 className="text-white font-bold text-lg">
                  {selectedSuggestedDestination.name}, {getLocalizedCountryLabel(selectedSuggestedDestination.country, wizardLanguage)}
                </h5>
                <p className="text-white/90 text-sm mt-1 max-w-2xl leading-relaxed">
                  {getLocalizedDestinationReason(
                    selectedSuggestedDestination.name,
                    selectedSuggestedDestination.reason,
                    wizardLanguage,
                    selectedSuggestedDestination.source,
                  )}
                </p>
                {selectedSuggestedDestination.fitAngle && (
                  <div className="mt-3">
                    <span className="inline-flex items-center rounded-full border border-[#C9A84C]/20 bg-[#C9A84C]/10 px-3 py-1.5 text-[#E8D4A0] text-xs font-semibold">
                      {selectedSuggestedDestination.fitAngle}
                    </span>
                  </div>
                )}
              </div>

              <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-white/90 text-xs font-semibold">
                <i className="ri-magic-line text-[#C9A84C]"></i>
                {formData.destinationSuggestionChoice
                  ? (wizardLanguage === 'en' ? 'Chosen destination' : 'Destino escolhido')
                  : (wizardLanguage === 'en' ? 'Best match' : 'Melhor opção')}
              </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-4">
              <div className="rounded-2xl border border-white/10 bg-[#07111F]/50 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <i className={`${selectedSuggestedDestination.icon} text-[#C9A84C] text-lg`}></i>
                  <span className="text-white font-semibold text-sm">{t('wizard.step10.compatibility')}</span>
                </div>

                <div className="space-y-2.5">
                  {recommendationHighlights.map((highlight) => (
                    <div key={highlight} className="flex items-start gap-2">
                      <i className="ri-check-line text-[#C9A84C] mt-0.5"></i>
                      <p className="text-white/90 text-sm leading-relaxed">{highlight}</p>
                    </div>
                  ))}
                </div>

                {recommendationTradeoff && (
                  <div className="mt-4 rounded-xl border border-[#C9A84C]/20 bg-[#C9A84C]/8 px-3 py-3">
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#E8D4A0]">
                      {wizardLanguage === 'en' ? 'Tradeoff' : 'Ressalva'}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-[#F0F5FB]">{recommendationTradeoff}</p>
                  </div>
                )}

                {recommendationConflicts.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {recommendationConflicts.map((conflict) => (
                      <div key={conflict} className="flex items-start gap-2 text-xs leading-relaxed text-amber-100">
                        <i className="ri-error-warning-line mt-0.5 text-[#C9A84C]"></i>
                        <span>{conflict}</span>
                      </div>
                    ))}
                  </div>
                )}

                {recommendationReviewChecks.length > 0 && (
                  <div className="mt-4 border-t border-white/10 pt-4">
                    <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-white/55">
                      {wizardLanguage === 'en' ? 'Trip review checks' : 'Checagens da viagem'}
                    </p>
                    <div className="grid gap-2">
                      {recommendationReviewChecks.map((check) => {
                        const tone =
                          check.status === 'aligned'
                            ? 'border-emerald-300/20 bg-emerald-400/10 text-emerald-100'
                            : check.status === 'caution'
                              ? 'border-[#C9A84C]/24 bg-[#C9A84C]/10 text-[#F1DFAE]'
                              : 'border-white/10 bg-white/5 text-[#D7E4F2]';
                        const icon =
                          check.status === 'aligned'
                            ? 'ri-check-line'
                            : check.status === 'caution'
                              ? 'ri-error-warning-line'
                              : 'ri-information-line';

                        return (
                          <div key={`${check.label}-${check.status}`} className={`rounded-xl border px-3 py-2 ${tone}`}>
                            <div className="flex items-start gap-2">
                              <i className={`${icon} mt-0.5`}></i>
                              <div>
                                <p className="text-xs font-bold leading-tight">{check.label}</p>
                                <p className="mt-0.5 text-xs leading-relaxed opacity-85">{check.detail}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-white/10 bg-[#07111F]/50 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-price-tag-3-line text-[#C9A84C]"></i>
                  <span className="text-white font-semibold text-sm">{t('wizard.step10.weightedSignals')}</span>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {recommendationMatchedSignals.map((signal) => (
                      <span
                        key={signal}
                        className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-white/90 text-xs font-medium"
                      >
                        {signal}
                      </span>
                    ))}
                </div>

                {recommendationUnknowns.length > 0 && (
                  <div className="border-t border-white/10 pt-3">
                    <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.16em] text-white/55">
                      {wizardLanguage === 'en' ? 'Still to confirm' : 'Ainda por confirmar'}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {recommendationUnknowns.map((unknown) => (
                        <span key={unknown} className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-[#D7E4F2]">
                          {unknown}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <p className="text-white/90 text-xs leading-relaxed">
                  {t('wizard.step10.continueDescription')}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-bold text-white/90 mb-2">
              {copy.departureDate} <span className="text-[#C9A84C]">*</span>
            </label>
            <input
              type="date"
              value={formData.dataInicio || ''}
              onChange={(e) => updateFormData({ dataInicio: e.target.value })}
              className="w-full px-4 py-3 text-sm bg-white/10 border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-[#C9A84C] outline-none transition-all"
            />
            {errors.dataInicio && <p className="text-red-400 text-xs mt-1">{errors.dataInicio}</p>}
          </div>
          <div>
            <label className="block text-sm font-bold text-white/90 mb-2">
              {copy.returnDate} <span className="text-[#C9A84C]">*</span>
            </label>
            <input
              type="date"
              value={formData.dataFim || ''}
              onChange={(e) => updateFormData({ dataFim: e.target.value })}
              className="w-full px-4 py-3 text-sm bg-white/10 border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-[#C9A84C] outline-none transition-all"
            />
            {errors.dataFim && <p className="text-red-400 text-xs mt-1">{errors.dataFim}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-bold text-white/90 mb-2">{copy.adults}</label>
            <select
              value={formData.numeroAdultos || '1'}
              onChange={(e) => updateFormData({ numeroAdultos: e.target.value })}
              className="w-full px-4 py-3 text-sm bg-white/10 border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-[#C9A84C] outline-none transition-all cursor-pointer"
            >
              {adultOptions.map((value) => (
                <option key={value} value={value} className="bg-[#0A1628]">
                  {value}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold text-white/90 mb-2">{copy.children}</label>
            <select
              value={formData.numeroCriancas || '0'}
              onChange={(e) => updateFormData({ numeroCriancas: e.target.value })}
              className="w-full px-4 py-3 text-sm bg-white/10 border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-[#C9A84C] outline-none transition-all cursor-pointer"
            >
              {childrenOptions.map((value) => (
                <option key={value} value={value} className="bg-[#0A1628]">
                  {value}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold text-white/90 mb-2">
              {copy.budgetPerPerson} <span className="text-[#C9A84C]">*</span>
            </label>
            <p className="text-white/75 text-xs mb-2">{copy.budgetUsdHint}</p>
            <select
              value={formData.orcamento || ''}
              onChange={(e) => updateFormData({ orcamento: e.target.value })}
              className="w-full px-4 py-3 text-sm bg-white/10 border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-[#C9A84C] outline-none transition-all cursor-pointer"
            >
              <option value="" className="bg-[#0A1628]">{copy.budgetPlaceholder}</option>
              {budgetOptions.map((budget) => (
                <option key={budget} value={budget} className="bg-[#0A1628]">
                  {budgetOptionDisplay.get(budget) ?? budget}
                </option>
              ))}
            </select>
            {errors.orcamento && <p className="text-red-400 text-xs mt-1">{errors.orcamento}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-bold text-white/90 mb-2">{copy.checkedBag}</label>
            <select
              value={formData.bagagemDespachada || ''}
              onChange={(e) => updateFormData({ bagagemDespachada: e.target.value })}
              className="w-full px-4 py-3 text-sm bg-white/10 border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-[#C9A84C] outline-none transition-all cursor-pointer"
            >
              <option value="" className="bg-[#0A1628]">{copy.choose}</option>
              <option value="sim" className="bg-[#0A1628]">{copy.checkedBagYes}</option>
              <option value="nao" className="bg-[#0A1628]">{copy.checkedBagNo}</option>
            </select>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#C9A84C]/10 flex-shrink-0">
              <i className="ri-car-line text-[#C9A84C] text-lg"></i>
            </div>
            <div>
              <p className="text-white font-bold text-sm">{copy.transportQuestion}</p>
              <p className="text-white/80 text-xs leading-relaxed mt-1">{copy.transportHelper}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {copy.transportOptions.map((option) => {
              const isSelected = formData.transportPreference === option.value;

              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() =>
                    updateFormData({
                      transportPreference: isSelected ? '' : option.value,
                    })
                  }
                  className={`rounded-xl border px-4 py-3 text-left text-sm font-semibold transition-all ${
                    isSelected
                      ? 'border-[#C9A84C]/30 bg-[#C9A84C]/10 text-[#E8D4A0]'
                      : 'border-white/10 bg-white/5 text-white/90 hover:bg-white/10'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <i className={isSelected ? 'ri-check-line text-[#C9A84C]' : 'ri-checkbox-blank-circle-line text-white/50'}></i>
                    {option.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* NOVOS CAMPOS - PROMPT 6 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-bold text-white/90 mb-2">{copy.flexibleDates}</label>
            <select
              value={formData.flexible_dates === true ? 'sim' : formData.flexible_dates === false ? 'nao' : ''}
              onChange={(e) => updateFormData({ flexible_dates: e.target.value === 'sim' ? true : e.target.value === 'nao' ? false : undefined })}
              className="w-full px-4 py-3 text-sm bg-white/10 border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-[#C9A84C] outline-none transition-all cursor-pointer"
            >
              <option value="" className="bg-[#0A1628]">{copy.choose}</option>
              <option value="sim" className="bg-[#0A1628]">{copy.yes}</option>
              <option value="nao" className="bg-[#0A1628]">{copy.no}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-bold text-white/90 mb-2">{copy.purchaseWindow}</label>
            <select
              value={formData.purchase_window || ''}
              onChange={(e) => updateFormData({ purchase_window: e.target.value })}
              className="w-full px-4 py-3 text-sm bg-white/10 border border-white/20 text-white rounded-xl focus:ring-2 focus:ring-[#C9A84C] outline-none transition-all cursor-pointer"
            >
              <option value="" className="bg-[#0A1628]">{copy.choose}</option>
              {purchaseWindowOptions.map((option) => (
                <option key={option.value} value={option.value} className="bg-[#0A1628]">
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Dados de contato */}
      <div className="space-y-4">
        <h4 className="text-white font-bold text-base flex items-center gap-2">
          <i className="ri-user-line text-[#C9A84C]"></i> {copy.contactTitle}
        </h4>
        <div>
          <label className="block text-sm font-bold text-white/90 mb-2">
            {copy.fullName} <span className="text-[#C9A84C]">*</span>
          </label>
          <div className="relative">
            <i className="ri-user-line absolute left-4 top-1/2 -translate-y-1/2 text-[#C9A84C]"></i>
            <input
              type="text"
              value={formData.fullName || ''}
              onChange={(e) => updateFormData({ fullName: e.target.value })}
              placeholder={copy.fullNamePlaceholder}
              className="w-full pl-11 pr-4 py-3 text-sm bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-xl focus:ring-2 focus:ring-[#C9A84C] outline-none transition-all"
            />
          </div>
          {errors.fullName && <p className="text-red-400 text-xs mt-1">{errors.fullName}</p>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-bold text-white/90 mb-2">
              {copy.email} <span className="text-[#C9A84C]">*</span>
            </label>
            <div className="relative">
              <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-[#C9A84C]"></i>
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => updateFormData({ email: e.target.value })}
                placeholder="seu@email.com"
                className="w-full pl-11 pr-4 py-3 text-sm bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-xl focus:ring-2 focus:ring-[#C9A84C] outline-none transition-all"
              />
            </div>
            {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
          </div>
          <div>
            <label className="block text-sm font-bold text-white/90 mb-2">
              {copy.whatsapp} <span className="text-[#C9A84C]">*</span>
            </label>
            <div className="relative">
              <i className="ri-whatsapp-line absolute left-4 top-1/2 -translate-y-1/2 text-[#C9A84C]"></i>
              <input
                type="tel"
                value={formData.phone || ''}
                onChange={(e) => updateFormData({ phone: e.target.value })}
                placeholder={copy.phonePlaceholder}
                className="w-full pl-11 pr-4 py-3 text-sm bg-white/10 border border-white/20 text-white placeholder-white/40 rounded-xl focus:ring-2 focus:ring-[#C9A84C] outline-none transition-all"
              />
            </div>
            <p className="text-white/75 text-xs mt-2 leading-relaxed">{copy.phoneHelper}</p>
            {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
          </div>
        </div>
      </div>

      {/* Aviso de privacidade */}
      <div className="p-4 bg-white/5 border border-white/10 rounded-xl flex items-start gap-3">
        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C9A84C]/10 flex-shrink-0">
          <i className="ri-shield-check-line text-[#C9A84C] text-sm"></i>
        </div>
        <p className="text-white/90 text-xs leading-relaxed">
          {copy.privacy} <a href="/privacy.html" className="text-[#E8D4A0] underline underline-offset-2">{wizardLanguage === 'en' ? 'Privacy Policy' : 'Política de Privacidade'}</a>
        </p>
      </div>
    </div>
  );
}
